import React from 'react';
import { motion } from 'framer-motion';
import { 
  Code, 
  Building2, 
  Headphones, 
  CheckCircle,
  Users,
  Target,
  Award,
  TrendingUp,
  Monitor,
  Briefcase,
  Globe,
  Zap
} from 'lucide-react';
import './ServicesPage.css';

const ServicesPage: React.FC = () => {
  const serviceCategories = [
    {
      id: 'it',
      icon: Code,
      title: 'IT Services',
      description: 'Comprehensive IT recruitment across all technology domains',
      color: '#2563eb',
      services: [
        'Testing & Quality Assurance',
        'Telecom & Wireless',
        'Internet Applications',
        'Cloud Computing',
        'Retail Financial Services',
        'Consumer Banking',
        'Enterprise Server',
        'Semiconductor',
        'Networking',
        'Virtualization',
        'E-commerce',
        'Storage',
        'Mobility',
        'Analytics',
        'Mass Media & Hospitality',
        'Manufacturing & Medical Transcription'
      ],
      features: [
        'Technical skill assessment',
        'Industry-specific expertise',
        'Rapid placement process',
        'Quality assurance'
      ]
    },
    {
      id: 'non-it',
      icon: Building2,
      title: 'Non-IT Services',
      description: 'Expert recruitment for traditional business sectors',
      color: '#059669',
      services: [
        'Services',
        'Food & Hospitality',
        'FMCG',
        'Private Equity',
        'Healthcare',
        'Banking & Finance',
        'Corporate Banking',
        'Investment Banking',
        'BFSI & Automotive',
        'Construction & Real Estate',
        'Media & Entertainment',
        'Retail',
        'Engineering & Manufacturing'
      ],
      features: [
        'Domain expertise',
        'Cultural fit assessment',
        'Long-term career planning',
        'Industry networking'
      ]
    },
    {
      id: 'ites',
      icon: Headphones,
      title: 'ITES Services',
      description: 'Complete business process outsourcing solutions',
      color: '#dc2626',
      services: [
        'Business Process Outsourcing (BPO)',
        'Knowledge Process Outsourcing (KPO)',
        'Inbound Technical Support',
        'Outbound Technical Support',
        'Inbound Customer Support',
        'Outbound Customer Support',
        'Chat Support',
        'Service Desk (L1, L2, L3)',
        'Back Office Operations'
      ],
      features: [
        '24/7 support capabilities',
        'Multi-language support',
        'Process optimization',
        'Cost-effective solutions'
      ]
    }
  ];

  const processSteps = [
    {
      icon: Target,
      title: 'Requirement Analysis',
      description: 'We analyze your specific needs and requirements to understand the perfect candidate profile.'
    },
    {
      icon: Users,
      title: 'Talent Sourcing',
      description: 'Our team scouts for the right talent using our extensive network and database.'
    },
    {
      icon: Award,
      title: 'Screening & Assessment',
      description: 'Comprehensive evaluation of candidates to ensure the best fit for your organization.'
    },
    {
      icon: TrendingUp,
      title: 'Placement & Follow-up',
      description: 'We facilitate the placement process and provide ongoing support for successful integration.'
    }
  ];

  const whyChooseUs = [
    {
      icon: Monitor,
      title: 'Tech-Driven Approach',
      description: 'Integrated technology-based solutions for efficient talent matching'
    },
    {
      icon: Briefcase,
      title: 'Industry Expertise',
      description: 'Deep understanding across IT, Non-IT, and ITES sectors'
    },
    {
      icon: Users,
      title: 'Personalized Service',
      description: 'Tailored recruitment solutions for each client and candidate'
    },
    {
      icon: Globe,
      title: 'Extensive Network',
      description: 'Access to a vast pool of qualified professionals'
    },
    {
      icon: Zap,
      title: 'Fast Results',
      description: 'Quick turnaround time without compromising on quality'
    },
    {
      icon: Award,
      title: 'Proven Track Record',
      description: 'Established success since 2013 with high placement rates'
    }
  ];

  return (
    <div className="services-page">
      {/* Hero Section */}
      <section className="services-hero section">
        <div className="container">
          <motion.div
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="hero-title">Our Services</h1>
            <p className="hero-subtitle">
              What you do has a far greater impact than what you say! We deliver 
              comprehensive recruitment solutions across multiple industries.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Service Categories */}
      <section className="service-categories section">
        <div className="container">
          {serviceCategories.map((category, index) => (
            <motion.div
              key={category.id}
              className="service-category"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="category-header">
                <div 
                  className="category-icon"
                  style={{ backgroundColor: category.color }}
                >
                  <category.icon size={40} />
                </div>
                <div className="category-info">
                  <h2>{category.title}</h2>
                  <p>{category.description}</p>
                </div>
              </div>

              <div className="category-content">
                <div className="services-list">
                  <h3>Our Service Areas:</h3>
                  <div className="services-grid">
                    {category.services.map((service, serviceIndex) => (
                      <motion.div
                        key={service}
                        className="service-item"
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3, delay: serviceIndex * 0.05 }}
                        viewport={{ once: true }}
                      >
                        <CheckCircle size={16} />
                        <span>{service}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>

                <div className="category-features">
                  <h3>What We Offer:</h3>
                  <div className="features-grid">
                    {category.features.map((feature, featureIndex) => (
                      <motion.div
                        key={feature}
                        className="feature-item"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: featureIndex * 0.1 }}
                        viewport={{ once: true }}
                      >
                        <div className="feature-bullet"></div>
                        <span>{feature}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Our Recruitment Process</h2>
            <p className="section-subtitle">
              A systematic approach to finding and placing the right talent
            </p>
          </motion.div>

          <div className="process-steps">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.title}
                className="process-step"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="step-number">{index + 1}</div>
                <div className="step-icon">
                  <step.icon size={32} />
                </div>
                <div className="step-content">
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="why-choose-section section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Why Choose Future Tech HR?</h2>
            <p className="section-subtitle">
              We don't just fill positions - we build successful careers and thriving organizations.
            </p>
          </motion.div>

          <div className="why-choose-grid">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={item.title}
                className="why-choose-card card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="card-icon">
                  <item.icon size={28} />
                </div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="services-cta section">
        <div className="container">
          <motion.div
            className="cta-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Ready to Get Started?</h2>
            <p>
              Whether you're looking for your next career opportunity or seeking top talent 
              for your organization, we're here to help you succeed.
            </p>
            <div className="cta-buttons">
              <a href="/#contact" className="btn btn-primary">
                Contact Us Today
              </a>
              <a href="/" className="btn btn-secondary">
                Learn More About Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
